import React, { useEffect, useState } from "react";
import ErrorMessage from "../../../core/common/ErrorMessage";
import { ErrorType } from "../../../types/common-type";
import CommonSelect2 from "../../../core/common/commonSelect2";
import { countryOptions } from "../../../core/data/js/common-data";
import EditIcon from "../../../core/svg-icons/EditIcon";

interface ModifyProps {
  handleClose: () => void;
  handleSubmit: (data: any) => void;
  applicant?: any;
  isSubmitting?: boolean;
} 
const EMAIL_REGEX =
  /^(?!.*\.\.)[A-Za-z0-9](?:[A-Za-z0-9._%+-]*[A-Za-z0-9])?@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;
 
const MOBILE_RULES: Record<string, { regex: RegExp; message: string }> = {
  "+91": {
    regex: /^[6-9][0-9]{9}$/,
    message: "Mobile number must be 10 digits and start with 6, 7, 8 or 9",
  },
  "+1": {
    regex: /^[2-9][0-9]{9}$/,
    message: "Mobile number must be 10 digits and start with 2-9",
  },
};
const DEFAULT_MOBILE_RULE = {
  regex: /^[0-9]{7,10}$/,
  message: "Please enter a valid mobile number",
};

const validateEmail = (value: string) => {
  const v = value.trim();
  if (!v) return "Email is required";
  if (v.length > 254) return "Email is too long";
  if (!EMAIL_REGEX.test(v)) return "Please enter a valid email address";
  return "";
};

const validatePhone = (value: string, countryCode: string) => {
  if (!value) return "Phone number is required";
  if (!/^[0-9]+$/.test(value)) return "Only digits are allowed";
  const rule = MOBILE_RULES[countryCode] ?? DEFAULT_MOBILE_RULE;
  if (!rule.regex.test(value)) return rule.message;
  return "";
};
 
const ModifyInviteModal: React.FC<ModifyProps> = ({
  handleClose,
  handleSubmit,
  applicant,
  isSubmitting = false,
}) => {
  const [errors, setErrors] = useState<ErrorType>();

  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    countryCode: "+91",
    reason: "",
  });

  useEffect(() => {
    if (applicant) {
      setFormData({
        email: applicant.email || "",
        phone: applicant.phone || "",
        countryCode: applicant.countryCode || "+91",
        reason: "",
      });
    }
  }, [applicant]);

  const handleModalSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: ErrorType = {};

    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    const phoneError = validatePhone(formData.phone, formData.countryCode);
    if (phoneError) newErrors.phone = phoneError;

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    handleSubmit({
      ...formData,
      email: formData.email.trim(),
      assignment_id: applicant?.assignment_id,
      case_id: applicant?.case_id,
    });
  };

  return (
    <>
      <div className="modal-backdrop fade show" />

      <div
        className="modal fade show"
        style={{ display: "block", background: "rgba(0,0,0,.25)" }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div
            className="modal-content border-0 shadow-lg"
            style={{ borderRadius: 18, overflow: "hidden" }}
          >
            {/* Header */}
            <div
              className="d-flex justify-content-between align-items-center px-4 py-3"
              style={{ background: "#f26522", color: "#fff" }}
            >
              <div className="d-flex align-items-center">
                <div
                  className="me-3 d-flex justify-content-center align-items-center"
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: 12,
                    background: "rgba(255,255,255,.15)",
                  }}
                >
                  <EditIcon />
                </div>

                <div>
                  <h4 className="mb-0 text-white">Modify Invite</h4>
                  <small className="text-white-80">
                    Update applicant details and resend invitation
                  </small>
                </div>
              </div>

              <button
                type="button"
                className="btn btn-light rounded-circle"
                style={{ width: 40, height: 40 }}
                onClick={handleClose}
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <form onSubmit={handleModalSubmit} noValidate>
              <div className="modal-body p-4">
                {/* Instruction */}
                <div
                  className="mb-4 p-3"
                  style={{
                    borderRadius: 12,
                    background: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                  }}
                >
                  <div className="fw-semibold mb-2 text-primary">
                    Instructions
                  </div>

                  <ul
                    className="mb-0 ps-3 text-muted"
                    style={{ listStyle: "initial" }}
                  >
                    <li>Modify phone number or email if required.</li>
                    <li>Provide a reason before resending the invite.</li>
                    <li>The applicant will receive a new invitation.</li>
                  </ul>
                </div>

                <div className="row">
                  {/* Email */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">
                      Email Address
                    </label>

                    <input
                      type="email"
                      className={`form-control rounded-3 ${
                        errors?.email ? "is-invalid" : ""
                      }`}
                      placeholder="Enter Email"
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        setErrors((prev) => ({ ...prev, email: "" }));
                      }}
                      onBlur={() =>
                        setErrors((prev) => ({
                          ...prev,
                          email: validateEmail(formData.email),
                        }))
                      }
                    />

                    <ErrorMessage message={errors?.email} />
                  </div>

                  {/* Phone */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">
                      Mobile Number
                    </label>

                    <div className="d-flex">
                      <div style={{ width: 120 }}>
                        <CommonSelect2
                          options={countryOptions}
                          defaultValue={formData.countryCode}
                          showValue
                          onChange={(option: any) => {
                            setFormData({
                              ...formData,
                              countryCode: option.value,
                            });
                            setErrors((prev) => ({ ...prev, phone: "" }));
                          }}
                        />
                      </div>

                      <input
                        className={`form-control rounded-3 ms-2 ${
                          errors?.phone ? "is-invalid" : ""
                        }`}
                        placeholder="9876543210"
                        inputMode="numeric"
                        maxLength={10}
                        value={formData.phone}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            phone: e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 10),
                          });
                          setErrors((prev) => ({ ...prev, phone: "" }));
                        }}
                        onBlur={() =>
                          setErrors((prev) => ({
                            ...prev,
                            phone: validatePhone(
                              formData.phone,
                              formData.countryCode,
                            ),
                          }))
                        }
                      />
                    </div>

                    <ErrorMessage message={errors?.phone} />
                  </div>
                </div>

                {/* Reason */}
                <div className="mb-2">
                  <label className="form-label fw-semibold">
                    Reason for Resending Invite
                  </label>

                  <textarea
                    rows={5}
                    className="form-control rounded-3"
                    placeholder="Please enter the reason..."
                    value={formData.reason}
                    onChange={(e) =>
                      setFormData({ ...formData, reason: e.target.value })
                    }
                  />

                  <ErrorMessage message={errors?.reason} />
                </div>
              </div>

              {/* Footer */}
              <div
                className="modal-footer border-0 px-4 pb-4"
                style={{ background: "#fff" }}
              >
                <button
                  type="button"
                  className="btn btn-light px-4"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-send me-2"></i>
                      Send Invite Again
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ModifyInviteModal; 